/**
 * Public-page nav for the app (/order, /packages, legal pages, 404).
 * Parity reference: apps/marketing/src/components/website/PublicNavV2.tsx (#440).
 * The link set stays deliberately minimal — in-app visitors are already converted,
 * so no marketing links, and no "Plan an Event" CTA on /order (mid-checkout).
 * "Order Online" goes to the form-first wizard while the cart is parked (#1221).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ExternalLink, Phone } from "lucide-react";
import { useAppSettings } from "@/hooks/useAppSettings";
import { useOrderSettings } from "@/hooks/useOrderSettings";
import { MARKETING_DOMAIN } from "@/lib/constants";
import { DISPLAY_PHONE, toTelHref } from "@/lib/phone";
import { LOGO_FALLBACK } from "@/lib/brand";
import { logoImageUrl } from "@/lib/seo";
import { ORDER_ONLINE_HREF } from "@/lib/ordering";

// Case-insensitive to match React Router, which renders OrderPage for /Order too.
const ORDER_ROUTE = /^\/order(\/|$)/i;

const CTA_CLASS =
  "bg-primary border border-[#4e5d43] text-white uppercase tracking-[0.14em] text-[0.78rem] px-4 rounded-none hover:bg-[#4e5d43] transition-all duration-300";
const CTA_STYLE = { fontFamily: "Manrope, sans-serif", fontWeight: 600 };
const MOBILE_ROW = "flex items-center gap-1 min-h-[44px]";

export function PublicNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { data: settings } = useAppSettings();
  const location = useLocation();
  const { settings: orderSettings } = useOrderSettings();

  const logoText = settings?.site_logo_text || "TRISTANO'S";
  const logoUrl = logoImageUrl(settings?.site_logo_url) || LOGO_FALLBACK;
  const marketingDomain = settings?.marketing_domain_url || MARKETING_DOMAIN;
  const settingTel = toTelHref(settings?.business_phone);
  const telHref = settingTel === "tel:" ? toTelHref(DISPLAY_PHONE) : settingTel;
  const showCta = !ORDER_ROUTE.test(location.pathname);

  // A href with a query (Order Online → /plan?service=drop_off) matches on the full URL.
  const isActive = (path: string) =>
    (path.includes("?") ? location.pathname + location.search : location.pathname) === path;
  const linkClass = (path: string) =>
    `hover:text-secondary transition-colors ${isActive(path) ? "text-white font-semibold" : ""}`;
  const close = () => setMobileOpen(false);

  // Lock body scroll while the mobile menu is open; restore the prior value on
  // close or unmount rather than hard-clearing it.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  // md:hidden hides the menu past 768px but the scroll lock would stay on, so
  // crossing to desktop width closes the menu.
  useEffect(() => {
    if (!mobileOpen) return;
    const mql = window.matchMedia("(min-width: 768px)");
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setMobileOpen(false);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [mobileOpen]);

  // Behave like a disclosure dialog (#913): opening moves focus to the first
  // item, and Escape closes the menu and hands focus back to the hamburger.
  // Skip an Escape another layer (e.g. a Radix sheet) already handled.
  useEffect(() => {
    if (!mobileOpen) return;
    menuRef.current?.querySelector<HTMLElement>("a[href], button")?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      setMobileOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  return (
    <>
      <nav className="bg-[hsl(0,0%,16%)] text-[hsl(40,33%,97%)] sticky top-0 z-50">
        <div className="max-w-[1440px] mx-auto flex items-center justify-between px-4 lg:px-6 py-2">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={logoUrl} alt={logoText} className="h-16 md:h-20 w-auto object-contain" />
          </Link>

          {/* Desktop */}
          <div className="hidden md:flex items-center gap-8 text-sm font-secondary">
            {orderSettings.order_enabled && (
              <Link to={ORDER_ONLINE_HREF} className={linkClass(ORDER_ONLINE_HREF)}>Order Online</Link>
            )}
            <a
              href={marketingDomain}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-secondary transition-colors"
            >
              Visit Website <ExternalLink className="h-3 w-3" />
            </a>
            <a href={telHref} className="flex items-center gap-1.5 hover:text-secondary transition-colors">
              <Phone className="h-3.5 w-3.5" /> Call
            </a>
            {showCta && (
              // Plain <a>, not <Link>: a full load lands /plan at the top; the app
              // has no scroll restoration, so a Link keeps the old scroll offset.
              <a href="/plan" className={`${CTA_CLASS} ml-1 py-1.5`} style={CTA_STYLE}>
                Plan an Event
              </a>
            )}
          </div>

          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-0.5">
            <a
              href={telHref}
              aria-label="Call Tristano's Catering"
              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <Phone className="h-5 w-5" />
            </a>
            <button
              ref={toggleRef}
              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            ref={menuRef}
            data-testid="public-nav-mobile-menu"
            className="md:hidden border-t border-[hsl(0,0%,25%)] px-4 pb-5 pt-2 font-secondary text-sm"
          >
            {orderSettings.order_enabled && (
              <Link to={ORDER_ONLINE_HREF} className={MOBILE_ROW} onClick={close}>
                Order Online
              </Link>
            )}
            <a
              href={marketingDomain}
              target="_blank"
              rel="noopener noreferrer"
              className={MOBILE_ROW}
              onClick={close}
            >
              Visit Website <ExternalLink className="h-3 w-3" />
            </a>
            {showCta && (
              <a
                href="/plan"
                className={`${CTA_CLASS} flex items-center justify-center min-h-[44px] mt-4 py-2.5`}
                style={CTA_STYLE}
                onClick={close}
              >
                Plan an Event
              </a>
            )}
            <a
              href={telHref}
              className="flex items-center justify-center gap-2 min-h-[44px] mt-3 border border-[hsl(0,0%,40%)] uppercase tracking-[0.14em] text-[0.78rem] px-4 py-2.5 hover:bg-[hsl(0,0%,26%)] transition-all duration-300"
              onClick={close}
            >
              <Phone className="h-4 w-4" /> Call us
            </a>
          </div>
        )}
      </nav>
      {/* Full-screen scrim behind the open mobile menu; tap to close. z-40 sits
          below the z-50 nav — don't raise the nav: /order's sheets portal at z-50. */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40" aria-hidden="true" onClick={close} />
      )}
    </>
  );
}
